import { useEffect, useId, useRef, useState } from 'react';
import { usePresence } from '../hooks/usePresence';
import { strings } from '../lib/strings';
import type { UniverseSort as SortValue } from '../lib/types';
import { CheckIcon, ChevronUpDownIcon, SortIcon } from './Icons';

const EXIT_MS = 180;

interface Props {
  value: SortValue;
  options: readonly SortValue[];
  onChange: (value: SortValue) => void;
  tone: 'dark' | 'light';
}

export function UniverseSort({ value, options, onChange, tone }: Props) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const { mounted, shown } = usePresence(open, EXIT_MS);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  useEffect(() => {
    if (!open || !mounted) return;
    itemRefs.current[active]?.focus({ preventScroll: true });
  }, [open, mounted, active]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openMenu = () => {
    setActive(Math.max(0, options.indexOf(value)));
    setOpen(true);
  };

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const choose = (option: SortValue) => {
    onChange(option);
    close(true);
  };

  const dark = tone === 'dark';

  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${strings.sortLabel}: ${strings.sorts[value]}`}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            openMenu();
          }
        }}
        className={`pressable flex h-[34px] items-center justify-center gap-1 rounded-full text-sm font-semibold ${
          dark
            ? 'bg-white/10 pl-3 pr-2 text-white/90 backdrop-blur-md hover:bg-white/15 hover:text-white'
            : 'w-[34px] bg-fill text-ink hover:bg-fill-strong sm:w-auto sm:pl-3 sm:pr-2'
        } ${open ? (dark ? 'bg-white/20' : 'bg-fill-strong') : ''}`}
      >
        <span aria-hidden="true" className="hidden sm:inline">
          {strings.sorts[value]}
        </span>
        {dark ? (
          <span aria-hidden="true" className="sm:hidden">
            {strings.sortLabel}
          </span>
        ) : (
          <SortIcon className="h-[18px] w-[18px] sm:hidden" />
        )}
        <ChevronUpDownIcon className={`h-4 w-4 opacity-60 ${dark ? '' : 'hidden sm:block'}`} />
      </button>
      {mounted && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={strings.sortLabel}
          className="absolute right-0 top-full z-popover mt-2 min-w-menu max-w-[calc(100vw-32px)] origin-top-right overflow-hidden rounded-md bg-material-thick py-1 shadow-overlay ring-1 ring-black/5 backdrop-blur-2xl backdrop-saturate-150"
          style={{
            opacity: shown ? 1 : 0,
            transform: shown ? 'none' : 'scale(0.72) translateY(-6px)',
            transition: shown
              ? 'transform 460ms var(--ease-spring), opacity 180ms var(--ease-standard)'
              : `transform ${EXIT_MS}ms var(--ease-standard), opacity ${EXIT_MS}ms var(--ease-standard)`,
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((i) => (i + 1) % options.length);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => (i - 1 + options.length) % options.length);
            } else if (e.key === 'Tab') {
              close(false);
            }
          }}
        >
          {options.map((option, i) => {
            const selected = option === value;
            return (
              <button
                key={option}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                tabIndex={i === active ? 0 : -1}
                onClick={() => choose(option)}
                className={`relative flex min-h-[44px] w-full items-center gap-2 pl-3 pr-4 text-left text-base text-ink transition-colors duration-fast hover:bg-fill focus-visible:bg-fill focus-visible:shadow-none ${
                  i > 0
                    ? 'before:absolute before:left-8 before:right-0 before:top-0 before:h-px before:bg-line'
                    : ''
                }`}
              >
                <span className="flex w-4 justify-center">
                  {selected && <CheckIcon className="h-4 w-4 text-primary" />}
                </span>
                <span className={selected ? 'font-semibold' : ''}>{strings.sorts[option]}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
